import { Capacitor } from '@capacitor/core';

/*
 * The phone app is this same React site running inside Capacitor. Pages load
 * from the device, so the API and web links point at the live site.
 */
export const isNativeApp = Capacitor.isNativePlatform();
export const SITE_URL = 'https://vidlyai.com';

/*
 * Signing in from the app. Google won't sign in inside an app's web view, so
 * the app opens the phone's browser at /app-login, which remembers this
 * browser tab is signing in the app and starts the normal Google sign-in.
 *
 * The token must not travel back in the app's link: another app can claim the
 * same link type and catch it. So (PKCE) the app makes a secret "verifier",
 * keeps it, and sends only its SHA-256 "challenge" to /app-login. When the tab
 * lands on /oauth2/callback, the website trades the token for a one-time code
 * bound to that challenge and hands the app the code. The app swaps the code
 * plus its verifier for the token (AppLoginController on the server). A caught
 * code is useless without the verifier.
 */
export const APP_SCHEME = 'com.vidlyai.app';
const APP_LOGIN_KEY = 'vidly.appLogin';
const VERIFIER_KEY = 'vidly.appLoginVerifier';

const b64url = (bytes) => btoa(String.fromCharCode(...new Uint8Array(bytes)))
  .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

/** base64url(SHA-256(verifier)): what the server checks the verifier against. */
export async function challengeFor(verifier) {
  return b64url(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier)));
}

/** In the app: make a fresh verifier and open the browser to sign in. */
export async function startAppLogin() {
  const verifier = b64url(crypto.getRandomValues(new Uint8Array(32)));
  try { localStorage.setItem(VERIFIER_KEY, verifier); } catch { /* kept in memory below */ }
  startAppLogin.verifier = verifier;
  const challenge = await challengeFor(verifier);
  const { Browser } = await import('@capacitor/browser');
  await Browser.open({ url: `${SITE_URL}/app-login?challenge=${challenge}`, presentationStyle: 'popover' });
}

/** On the website (/app-login): remember this tab is signing in the app, then go to Google. */
export function beginAppLoginInBrowser(apiBase, challenge) {
  try {
    sessionStorage.setItem(APP_LOGIN_KEY, JSON.stringify({ challenge, at: Date.now() }));
  } catch { /* private mode: falls back to a web sign-in */ }
  window.location.replace(`${apiBase}/oauth2/authorization/google`);
}

/** On the website (/oauth2/callback): the app's challenge if the app started this sign-in, else null. Only answers once. */
export function takeAppLogin() {
  try {
    const saved = JSON.parse(sessionStorage.getItem(APP_LOGIN_KEY) || 'null');
    sessionStorage.removeItem(APP_LOGIN_KEY);
    const fresh = saved && Date.now() - saved.at < 15 * 60 * 1000;
    return fresh && /^[A-Za-z0-9_-]{43}$/.test(saved.challenge || '') ? saved.challenge : null;
  } catch { return null; }
}

/** On the website: trade the fresh token for a one-time code for the app. */
export async function appLoginCode(apiBase, token, challenge) {
  const res = await fetch(`${apiBase}/auth/app/code`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ challenge }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.code) throw new Error(data.error || 'Could not hand the sign-in to the app');
  return data.code;
}

export const appLoginUrl = (code) => `${APP_SCHEME}://auth?code=${encodeURIComponent(code)}`;

/** In the app: swap a code from the browser (and our verifier) for the token. */
async function tokenForCode(apiBase, code) {
  let verifier = startAppLogin.verifier;
  try { verifier = verifier || localStorage.getItem(VERIFIER_KEY); } catch { /* none saved */ }
  if (!verifier) throw new Error('No sign-in in progress');
  const res = await fetch(`${apiBase}/auth/app/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code, verifier }),
  });
  const data = await res.json().catch(() => ({}));
  try { localStorage.removeItem(VERIFIER_KEY); } catch { /* fine */ }
  startAppLogin.verifier = null;
  if (!res.ok || !data.token) throw new Error(data.error || 'Sign-in failed');
  return data.token;
}

/**
 * In the app: when the browser hands a sign-in back, get the token and call
 * onToken(token) (or onError). Returns an unsubscribe function.
 */
export function listenForAppLogin(apiBase, onToken, onError) {
  if (!isNativeApp) return () => {};
  let handle;
  let cancelled = false;
  (async () => {
    const [{ App }, { Browser }] = await Promise.all([import('@capacitor/app'), import('@capacitor/browser')]);
    const h = await App.addListener('appUrlOpen', ({ url }) => {
      let parsed;
      try { parsed = new URL(url); } catch { return; }
      if (parsed.protocol !== `${APP_SCHEME}:` || parsed.host !== 'auth') return;
      const code = parsed.searchParams.get('code');
      Browser.close().catch(() => {});
      if (code) tokenForCode(apiBase, code).then(onToken, onError);
    });
    if (cancelled) h.remove(); else handle = h;
  })();
  return () => { cancelled = true; if (handle) handle.remove(); };
}

/*
 * Keeping the sign-in. The site keeps its token in localStorage; in the app
 * that lives in the web view, which Android may clear when it closes the app.
 * The app also keeps a copy in the phone's own app storage (Preferences):
 * restored on launch, and updated whenever the sign-in changes.
 */
const TOKEN_KEY = 'token';

export async function restoreSavedLogin() {
  if (!isNativeApp) return;
  try {
    const { Preferences } = await import('@capacitor/preferences');
    const { value } = await Preferences.get({ key: TOKEN_KEY });
    if (value && !localStorage.getItem(TOKEN_KEY)) localStorage.setItem(TOKEN_KEY, value);
  } catch { /* no saved copy: sign in as usual */ }
}

/** Copy the current sign-in (or its absence, after signing out) to the phone's storage. */
export async function saveLogin() {
  if (!isNativeApp) return;
  try {
    const { Preferences } = await import('@capacitor/preferences');
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) await Preferences.set({ key: TOKEN_KEY, value: token });
    else await Preferences.remove({ key: TOKEN_KEY });
  } catch { /* best effort */ }
}

/** Share a reel: the system share sheet in the app, the Web Share API in a browser. */
export async function shareLink({ title, url }) {
  if (isNativeApp) {
    const { Share } = await import('@capacitor/share');
    return Share.share({ title, url, dialogTitle: 'Share your reel' });
  }
  return navigator.share({ title, url });
}
export const canShare = isNativeApp || (typeof navigator !== 'undefined' && !!navigator.share);

/*
 * Sharing the video itself, not a link. In the app the finished reel is
 * downloaded to the app's cache and handed to the system share sheet, which
 * offers Save Video (iPhone) / Photos (Android) and posting straight to
 * Instagram, TikTok and the rest. Falls back to sharing the link if the
 * download fails. Cancelling the sheet is not an error: returns false then,
 * true once shared.
 */
export async function shareVideo({ title, url }) {
  if (!isNativeApp) return shareLink({ title, url });
  const [{ Filesystem, Directory }, { FileTransfer }, { Share }] = await Promise.all([
    import('@capacitor/filesystem'), import('@capacitor/file-transfer'), import('@capacitor/share')]);
  let uri;
  try {
    const name = `${(title || 'reel').replace(/[^\w-]+/g, '_').slice(0, 60) || 'reel'}.mp4`;
    ({ uri } = await Filesystem.getUri({ directory: Directory.Cache, path: name }));
    await FileTransfer.downloadFile({ url, path: uri });
  } catch {
    return shareLink({ title, url });
  }
  try {
    await Share.share({ title, files: [uri], dialogTitle: 'Save or share your reel' });
    return true;
  } catch (e) {
    if (!/cancel/i.test(e?.message || '')) throw e;
    return false;
  }
}

/** A small buzz in the app ('tap' for buttons, 'success' when a reel is ready). No-op on the web. */
export async function haptic(kind = 'tap') {
  if (!isNativeApp) return;
  try {
    const { Haptics, ImpactStyle, NotificationType } = await import('@capacitor/haptics');
    if (kind === 'success') await Haptics.notification({ type: NotificationType.Success });
    else await Haptics.impact({ style: ImpactStyle.Light });
  } catch { /* no vibration motor */ }
}
